import ApplicationError from 'es/app/business-logic/ApplicationError';
import fetchLoginUrl from 'es/app/business-logic/services/security/fetchLoginUrl';
import { GlobalStores } from 'es/app/store/GlobalStore';
import AppSettings from 'es/core/AppSettings';

import LogManager from 'es/core/logging/LogManager';
import AuthParameters from 'es/core/net/AuthParameters';
import qs from 'query-string';
import urlJoin from 'url-join';
import HttpResponse from './HttpResponse';
import Site from 'es/app/business-logic/domain-models/Site';

const logger = LogManager.getLogger('http');

interface IFetchOptionsOverrides<T> {
    responseInterceptor?: (response: HttpResponse<T>) => HttpResponse<T>;
}

const resolveCompanyAndFacilityAlias = (useCompanyAlias?: string, useFacilityAlias?: string) => {
    let companyAlias = useCompanyAlias;
    let facilityAlias = useFacilityAlias;

    if (!useCompanyAlias) {
        const authContext = getCurrentAuthContext();
        companyAlias = authContext.companyAlias;
        facilityAlias = authContext.facilityAlias;
    }

    return {
        companyAlias: companyAlias ?? Site.GlobalAlias,
        facilityAlias: facilityAlias ?? Site.GlobalAlias,
    };
};

const httpClient = {
    usePublicApi: {
        asAnonymous: anonymousHttpClient(AppSettings.publicApiUrl),
        asAuthorised: (useCompanyAlias?: string, useFacilityAlias?: string) => {
            const { companyAlias, facilityAlias } = resolveCompanyAndFacilityAlias(useCompanyAlias, useFacilityAlias);
            return authorisedHttpClient(AppSettings.publicApiUrl)(companyAlias, facilityAlias);
        },
        withCredentials: (companyAlias?: string, facilityAlias?: string) => {
            return authorisedHttpClient(AppSettings.publicApiUrl)(companyAlias, facilityAlias);
        },
    },
    useInternalApi: {
        anonymous: anonymousHttpClient(AppSettings.apiUrl),
        asAuthorised: (useCompanyAlias?: string, useFacilityAlias?: string) => {
            const { companyAlias, facilityAlias } = resolveCompanyAndFacilityAlias(useCompanyAlias, useFacilityAlias);
            return authorisedHttpClient(AppSettings.apiUrl)(companyAlias, facilityAlias);
        },
        withCredentials: (companyAlias?: string, facilityAlias?: string) => {
            return authorisedHttpClient(AppSettings.apiUrl)(companyAlias, facilityAlias);
        },
    },
    useCumulusApi: {
        anonymous: anonymousHttpClient(AppSettings.cumulusApiUrl),
        asAuthorised: () => {
            const { companyAlias, facilityAlias } = getCurrentAuthContext();
            return authorisedHttpClient(AppSettings.cumulusApiUrl)(companyAlias, facilityAlias);
        },
        withCredentials: (companyAlias?: string, facilityAlias?: string) => {
            return authorisedHttpClient(AppSettings.cumulusApiUrl)(companyAlias, facilityAlias);
        },
    },
};

export default httpClient;

////////////////////

const NO_CONTENT = { data: null, messages: [] };

function anonymousHttpClient(baseUrl: string) {
    return () => {
        const httpGet = <T = any>(relativeUrl: string, queryStringParams?: object) => {
            const url = buildUrl(baseUrl, relativeUrl, queryStringParams);
            return anonymousHttpGet<T>(url);
        };

        const httpPost = <T = any>(relativeUrl: string, body: unknown) => {
            const url = buildUrl(baseUrl, relativeUrl);
            return anonymousHttpPost<T>(url, body);
        };

        return {
            get: httpGet,
            post: httpPost,
        };
    };

    /////

    function anonymousHttpGet<T = any>(url: string) {
        return fetchAsync<T>(url, {
            method: 'GET',
            credentials: 'include',
            responseInterceptor: response => response,
        });
    }

    function anonymousHttpPost<T = any>(url: string, body: unknown) {
        return fetchAsync<T>(url, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
            responseInterceptor: response => response,
        });
    }
}

function authorisedHttpClient(baseUrl: string) {
    return (companyAlias?: string, facilityAlias?: string) => {
        const httpGet = <T = any>(
            relativeUrl: string,
            queryStringParams?: object | null,
            fetchOptions?: IFetchOptionsOverrides<T>
        ) => {
            const url = buildUrl(baseUrl, relativeUrl, queryStringParams);
            return authorizedHttpGet<T>(url, companyAlias, facilityAlias, fetchOptions);
        };

        const httpPost = <T = any>(relativeUrl: string, body: unknown, fetchOptions?: IFetchOptionsOverrides<T>) => {
            const url = buildUrl(baseUrl, relativeUrl);
            return authorizedHttpUpdate<T>('POST', url, body, companyAlias, facilityAlias, fetchOptions);
        };

        const httpPut = <T = any>(relativeUrl: string, body: unknown, fetchOptions?: IFetchOptionsOverrides<T>) => {
            const url = buildUrl(baseUrl, relativeUrl);
            return authorizedHttpUpdate<T>('PUT', url, body, companyAlias, facilityAlias, fetchOptions);
        };

        const httpDelete = <T = any>(
            relativeUrl: string,
            queryStringParams?: object,
            fetchOptions?: IFetchOptionsOverrides<T>
        ) => {
            const url = buildUrl(baseUrl, relativeUrl, queryStringParams);
            return authorizedHttpDelete<T>(url, companyAlias, facilityAlias, fetchOptions);
        };

        return {
            get: httpGet,
            post: httpPost,
            put: httpPut,
            delete: httpDelete,
        };
    };

    /////

    function authorizedHttpGet<T = any>(
        url: string,
        companyAlias?: string,
        facilityAlias?: string,
        fetchOptions?: IFetchOptionsOverrides<T>
    ) {
        return fetchAsync<T>(url, {
            headers: AuthParameters.createRequestHeaders(companyAlias, facilityAlias),
            method: 'GET',
            credentials: 'include',
            responseInterceptor: authorizedResponseInterceptor,
            ...(fetchOptions || {}),
        });
    }

    function authorizedHttpUpdate<T = any>(
        verb: 'POST' | 'PUT' | 'PATCH',
        url: string,
        body: unknown,
        companyAlias?: string,
        facilityAlias?: string,
        fetchOptions?: IFetchOptionsOverrides<T>
    ) {
        return fetchAsync<T>(url, {
            method: verb,
            credentials: 'include',
            headers: {
                ...AuthParameters.createRequestHeaders(companyAlias, facilityAlias),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
            responseInterceptor: authorizedResponseInterceptor,
            ...(fetchOptions || {}),
        });
    }

    function authorizedHttpDelete<T = any>(
        url: string,
        companyAlias?: string,
        facilityAlias?: string,
        fetchOptions?: IFetchOptionsOverrides<T>
    ) {
        return fetchAsync<T>(url, {
            method: 'DELETE',
            credentials: 'include',
            headers: {
                ...AuthParameters.createRequestHeaders(companyAlias, facilityAlias),
                'Content-Type': 'application/json',
            },
            responseInterceptor: authorizedResponseInterceptor,
            ...(fetchOptions || {}),
        });
    }

    function authorizedResponseInterceptor<D extends unknown, R extends { status: number; data: D }>(response: R) {
        if (response.status === 401) {
            window.location.href = fetchLoginUrl(window.location.href);
        }

        if (response.status === 500) {
            if (!response.data || typeof response.data !== 'object') {
                ApplicationError.raiseError('An unhandled error occurred.', response.data?.toString());
            } else {
                const { message, exceptionMessage, stackTrace } = getInnerMostException(response.data);

                const details = exceptionMessage || stackTrace ? `${exceptionMessage}\n${stackTrace}` : null;

                ApplicationError.raiseError(message, details);
            }
        }

        return response;
    }

    function getInnerMostException(exception: Record<string, any>) {
        if ('innerException' in exception) {
            return getInnerMostException(exception.innerException);
        }
        return exception;
    }
}

const buildUrl = (urlBase: string, relativeUrl: string, queryStringParameters: object | null = {}): string => {
    const params = queryStringParameters && qs.stringify(queryStringParameters);
    return `${urlJoin(urlBase, relativeUrl)}${params ? `?${params}` : ''}`;
};

async function fetchAsync<T = any>(url: string, fetchOptions: Parameters<typeof fetch>[1] & IFetchOptionsOverrides<T>) {
    try {
        const fetchResult = await fetch(url, fetchOptions);
        const response = new HttpResponse(fetchResult.status, await extractResponseBody(fetchResult));
        if (!fetchOptions.responseInterceptor) return response;
        return fetchOptions.responseInterceptor(response);
    } catch (error) {
        return new HttpResponse(-1, null, error);
    }

    async function extractResponseBody(fetchResult: Response) {
        try {
            if ((fetchResult.ok && fetchResult.status === 204) || fetchResult.statusText === 'No Content') {
                return NO_CONTENT;
            }

            try {
                return await fetchResult.json();
            } catch (jsonError) {
                return await fetchResult.text();
            }
        } catch (err) {
            logger.warn('Could not parse result body after fetch', fetchResult, err);
            return null;
        }
    }
}

function getCurrentAuthContext() {
    const store = GlobalStores.userStore;
    const { activeSite } = store;
    const { companyAlias = Site.GlobalAlias, facilityAlias = Site.GlobalAlias } = activeSite || {};
    return {
        companyAlias,
        facilityAlias,
    };
}
